import { useAppDispatch } from "@app/hooks";
import { EmeraldCheckbox } from "@emerald-react/checkbox";
import { setOpenPropertiesDrawer as setOpenPropertiesDrawerAction } from "@features/survey/surveySlice";
import useSurveyState from "@hooks/useSurveyState";
import { useUpdateSurveyFlagsMutation } from "@services/survey-core";
import { DetailedSurvey } from "@survey-types/survey.core.types";
import { updateCurrentSurvey } from "@survey/surveySlice";
import React, { useState } from "react";
import EditColumns from "./EditColumns";

// Type for property flags that can be toggled
type PropertyFlag = Pick<
  DetailedSurvey,
  "isIncludeSitePlansPC" | "isIncludeFlyersPC"
>;
type PropertyFlagKey = keyof PropertyFlag;

const PropertyComparisonSection = () => {
  const { survey } = useSurveyState();
  const dispatch = useAppDispatch();
  const [updateSurveyFlags] = useUpdateSurveyFlagsMutation();
  const [isEditColumnsOpen, setIsEditColumnsOpen] = useState(false);

  const toggleOpenEditColumns = (e?: React.MouseEvent) => {
    e?.preventDefault();
    setIsEditColumnsOpen(!isEditColumnsOpen);
  };

  const handleOpenPropertiesDrawer = (e?: React.MouseEvent) => {
    e?.preventDefault();
    dispatch(setOpenPropertiesDrawerAction(true));
  };

  const updateShowCurrentLocation = (toggle: boolean) => {
    updateSurveyFlags({ id: survey?.id, showCurrentLocation: toggle });
  };

  const handlePropertyFlagChange = (flag: PropertyFlagKey, value: boolean) => {
    dispatch(updateCurrentSurvey({ [flag]: value }));
    updateSurveyFlags({ id: survey?.id, [flag]: value })
      .unwrap()
      .catch((_error: unknown) => {
        console.error("Failed to update survey");
        dispatch(updateCurrentSurvey({ [flag]: !value }));
      });
  };

  return survey ? (
    <section className="flex flex-col gap-y-2 w-full text-sm">
      <div>
        You can change the order or visibility of property data by{" "}
        <a
          href="#"
          className="text-primary underline"
          onClick={toggleOpenEditColumns}
        >
          editing the columns
        </a>{" "}
        for this survey.
      </div>
      <hr className="border-divider-3 my-2" />
      <div className="text-data pt-2">Enable Document Links</div>
      <div className="text-data text-sm">
        To include links to Floor Plans & Flyers, select check boxes below. For
        the PDF Survey, links will display file names. To edit the file name,
        open your{" "}
        <a
          href="#"
          className="text-primary underline"
          onClick={handleOpenPropertiesDrawer}
        >
          property details.
        </a>
      </div>

      <EmeraldCheckbox
        label="Show Floor Plans Links"
        checked={survey.isIncludeSitePlansPC}
        onChange={(e) => handlePropertyFlagChange("isIncludeSitePlansPC", e)}
        className="pt-2"
      />

      <EmeraldCheckbox
        label="Show Property Flyer Links"
        checked={survey.isIncludeFlyersPC}
        onChange={(e) => handlePropertyFlagChange("isIncludeFlyersPC", e)}
      />

      <hr className="border-divider-3 my-2" />

      <div>Current Location</div>

      <div>
        To designate or add current location, open your{" "}
        <a
          href="#"
          className="text-primary underline"
          onClick={handleOpenPropertiesDrawer}
        >
          property details.
        </a>
      </div>

      <EmeraldCheckbox
        checked={survey.showCurrentLocation}
        label="Show Current Location"
        disabled={
          survey.properties.filter((property) => property.isCurrentLocation)
            .length === 0
        }
        onChange={updateShowCurrentLocation}
      />

      <EditColumns isOpen={isEditColumnsOpen} onClose={toggleOpenEditColumns} />
    </section>
  ) : null;
};

export default PropertyComparisonSection;
